/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  Checkbox,
  DatePicker,
  Flex,
  FormInstance,
  Input,
  Radio,
  Select,
  TimePicker,
  Upload,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import CountryDropdown from 'components/shared/CountryDropdown';
import { useLanguage } from 'hooks/Language/useLanguage';
import {
  IDynamicInputMeta,
  InputProps,
} from 'interfaces/otherInterface/dynamicInput';
import { PlusOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import InputAmount from './InputAmount';

const uploadFileUrl =
  process.env.REACT_APP_FILE_SERVICES_URL + '/files';

const formatTime = 'HH:mm';
const formatDate = 'YYYY-MM-DD';

const DynamicForm: React.FC<{
  form?: FormInstance<any>;
  meta?: IDynamicInputMeta;
  inputs: InputProps[];
  verifyAccount?: boolean;
  isVerifyAccountSuccess?: boolean;
  amountRequired?: boolean;
  fixedInputValue?: string | number;
  countries?: any;
  loadingCountry?: boolean;
  defaultCountry?: string;
  provider?: any;
  currency?: string;
  onAmountTypeChange?: (amountType: string | undefined) => void;
  setFixedInputValue?: (
    _e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onChangeFixedInputValue?: (
    _e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  handleInputChange?: (
    _field?: any,
    _event?: any,
    _data?: any,
  ) => void;
}> = ({
  meta,
  inputs,
  form,
  handleInputChange,
  verifyAccount = false,
  amountRequired = true,
  fixedInputValue,
  onChangeFixedInputValue,
  countries,
  loadingCountry,
  defaultCountry,
  provider,
  setFixedInputValue,
  isVerifyAccountSuccess,
  currency,
  onAmountTypeChange,
}) => {
  const { translate } = useLanguage();
  const [amountType, setAmountType] = useState<
    'FIXED' | 'custom' | 'done' | 'fixed' | 'CUSTOM'
  >();
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    setAmountType(undefined);
    onAmountTypeChange?.(undefined);
    form?.resetFields();
  }, [provider]);

  useEffect(() => {
    if (!amountType && meta?.amountType) {
      onAmountTypeChange?.(meta?.amountType);
    }
  }, [meta?.amountType]);

  // Upload file Like PNG,....
  const handleChangeImage = ({ file, fileList }: any) => {
    setFileList(fileList);

    if (file.status === 'done') {
      const url = file?.response?.data?.[0]?.url;
      form?.setFieldsValue({
        picture: url,
      });
    }
  };

  const beforeUpload = (_file: any) => {
    return true;
  };

  // Timer Picker format
  const handleTimeChange = (time: {
    format: (_arg0: string) => any;
  }) => {
    if (time) {
      const formattedTime = time.format(formatTime);

      form?.setFieldsValue({
        mytime: formattedTime,
      });
    }
  };

  const handleDateChange = (date: {
    format: (_arg0: string) => any;
  }) => {
    if (date) {
      const formattedDate = date.format(formatDate);

      form?.setFieldsValue({
        myDate: formattedDate,
      });
    }
  };

  const renderInputField = (
    field: InputProps | any,
    index: number,
    countries?: any,
    loadingCountry?: boolean,
    defaultCountry?: string,
  ) => {
    switch (field.type) {
      case 'dropdown':
        return (
          <Select
            key={field.key}
            size={'large'}
            style={{ width: '100%' }}
            options={selectSubOptions(field.options, provider)}
            placeholder={translate(field.label)}
            value={field.value}
            mode={field?.multi ? 'multiple' : undefined}
            onChange={(event, data: any) => {
              handleInputChange?.(index, event, data);
              setAmountType(data?.amountType);
              onAmountTypeChange?.(data?.amountType);
              setFixedInputValue?.(data?.amount);
              if (data?.amount !== undefined) {
                form?.setFieldsValue({ Amount: data?.amount });
              }
            }}
            aria-hidden={field?.hidden}
          />
        );
      case 'checkbox':
        if (field?.multi || field?.options?.length > 0) {
          return (
            <Checkbox.Group
              key={field?.key}
              disabled={field?.disabled}
              options={field?.options?.map((item: any) => ({
                label: translate(item?.text),
                value: item?.value,
                disabled: item?.disabled ?? false,
              }))}
            />
          );
        } else {
          return (
            <Checkbox key={field?.key} disabled={field?.disabled} />
          );
        }

      case 'radio':
        return (
          <Radio.Group
            options={field.options}
            value={field.value}
            aria-hidden={field?.hidden}
          >
            {/* Render radio options here */}
          </Radio.Group>
        );
      case 'textarea':
        return (
          <TextArea
            rows={field.rows}
            value={field.value}
            style={{ width: '100%' }}
            placeholder={translate(field.label)}
            onChange={(event) => handleInputChange?.(index, event)}
            aria-hidden={field?.hidden}
            minLength={field?.minLength}
            maxLength={field?.maxLength}
          />
        );
      case 'file':
        return (
          <Upload
            key={field?.key}
            action={uploadFileUrl}
            listType="picture-card"
            showUploadList={{ showDownloadIcon: false }}
            beforeUpload={beforeUpload}
            fileList={fileList}
            onChange={handleChangeImage}
          >
            {FileList.length >= 1 ? null : (
              <button
                style={{ border: 0, background: 'none' }}
                type="button"
              >
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>
                  {translate('Upload')}
                </div>
              </button>
            )}
          </Upload>
        );
      case 'tel':
        return (
          <Input
            size={'large'}
            type={field.type}
            min={field.min}
            max={field.max}
            value={field.value}
            aria-hidden={field?.hidden}
            minLength={field?.minLength}
            maxLength={field?.maxLength || 14}
            addonBefore={
              <CountryDropdown
                loading={loadingCountry}
                countries={countries}
                displayPhoneNumberInput
                className="custom-ant-select"
                showDefaultValue={true}
                defaultCountry={defaultCountry}
                disable
              />
            }
            prefix={
              countries?.length ? (
                <span style={{ marginTop: '3px' }}>
                  {countries[0]?.PhoneAreaCode}
                </span>
              ) : (
                <span />
              )
            }
            placeholder={translate(field.label)}
            onChange={(event) => handleInputChange?.(index, event)}
          />
        );
      case 'text':
        return (
          <Input
            size={'large'}
            type={field.type}
            min={field.min}
            max={field.max}
            value={field.value}
            disabled={field.disabled}
            placeholder={translate(field.label)}
            onChange={(event) => handleInputChange?.(index, event)}
            aria-hidden={field?.hidden}
            minLength={field?.minLength}
            maxLength={field?.maxLength}
          />
        );
      case 'number':
        return (
          <Input
            size={'large'}
            type={field.type}
            min={field.min}
            max={field.max}
            value={field.value || ''}
            placeholder={translate(field.label)}
            onChange={(event) => handleInputChange?.(index, event)}
            aria-hidden={field?.hidden}
            minLength={field?.minLength}
            maxLength={field?.maxLength}
          />
        );

      case 'time':
        return (
          <TimePicker
            size={'large'}
            key={field?.key}
            defaultValue={dayjs('12:08', formatTime)}
            format={formatTime}
            onChange={handleTimeChange}
            placeholder={'12:08'}
            style={{ width: '50%' }}
          />
        );

      case 'date':
        return (
          <DatePicker
            size={'large'}
            key={field?.key}
            format={formatDate}
            defaultValue={dayjs('2023-08-13', formatDate)}
            onChange={handleDateChange}
            placeholder={'2023-08-13'}
            style={{ width: '50%' }}
          />
        );

      case 'textArea':
        return (
          <Input.TextArea
            key={field?.key}
            placeholder={field?.label}
          />
        );

      default:
        return (
          <Input
            size={'large'}
            type={field.type}
            min={field.min}
            max={field.max}
            value={field.value}
            placeholder={translate(field.label)}
            onChange={(event) => handleInputChange?.(index, event)}
            aria-hidden={field?.hidden}
            disabled={field?.disabled}
            minLength={field?.minLength}
            maxLength={field?.maxLength}
          />
        );
    }
  };

  const isMetaAmountTypeCustom =
    meta?.amountType?.toLowerCase() === 'custom';
  const isMetaAmountTypeDone =
    meta?.amountType?.toLowerCase() === 'done';
  const isMetaAmountTypeFixed =
    meta?.amountType?.toLowerCase() === 'fixed';
  const isAmountTypeFixed = amountType?.toLowerCase() === 'fixed';
  const isAmountTypeCustom = amountType?.toLowerCase() === 'custom';
  const isAccountVerificationRequired = Boolean(
    verifyAccount === true ||
      String(verifyAccount).toLowerCase() === 'true' ||
      String(verifyAccount).toUpperCase() === 'YES',
  );

  const shouldDisableAmountField =
    isAmountTypeFixed ||
    isMetaAmountTypeDone ||
    isMetaAmountTypeFixed;

  const showAmountField =
    isMetaAmountTypeCustom || isAmountTypeCustom;

  const noAmountTypeSpecified =
    !shouldDisableAmountField && !showAmountField;

  const bothCheckAmountTypeAreTrue =
    shouldDisableAmountField && showAmountField;

  const showInputAmount =
    (amountRequired &&
      isAccountVerificationRequired &&
      isVerifyAccountSuccess) ||
    (amountRequired && !isAccountVerificationRequired);

  return (
    <InputAmount
      inputs={inputs}
      currency={currency}
      renderInputField={renderInputField}
      countries={countries}
      loadingCountry={loadingCountry}
      defaultCountry={defaultCountry}
      showInputAmount={showInputAmount}
      bothCheckAmountTypeAreTrue={bothCheckAmountTypeAreTrue}
      fixedInputValue={fixedInputValue}
      onChangeFixedInputValue={onChangeFixedInputValue}
      noAmountTypeSpecified={noAmountTypeSpecified}
      shouldDisableAmountField={shouldDisableAmountField}
      showAmountField={showAmountField}
    />
  );
};

const selectSubOptions = (item: any, provider: any) => {
  const filterItems = item?.map(
    (
      item: { value: string; logo: any; text: string },
      key: string,
    ) => ({
      ...item,
      key,
      value: item?.value,
      label: (
        <Flex gap="small" align="center" key={key}>
          <Avatar size="small" src={item?.logo || provider?.Logo} />
          <span
            className="text-overflow-ellipsis"
            style={{ width: '90%' }}
          >
            {item?.text}
          </span>
        </Flex>
      ),
    }),
  );
  return filterItems;
};

export default DynamicForm;
